import {
  normalizeSolarPayload,
  SOLAR_SITES_API,
  type SolarSite,
} from '$lib/solarSites';
import { exponentialBackoff } from '$lib/backoff';

type SolarSitesStatus = 'idle' | 'loading' | 'ok' | 'stale';

const SOLAR_POLL_MS = 60_000;
const MAX_BACKOFF_MS = 5 * 60_000;

export function nextSolarDelay(failures: number): number {
  return exponentialBackoff(failures, SOLAR_POLL_MS, MAX_BACKOFF_MS);
}

let all = $state<SolarSite[]>([]);
let status = $state<SolarSitesStatus>('idle');
let error = $state<string | null>(null);
let inventoryReason = $state<string | null>(null);
let rejected = $state(0);
let lastUpdated = $state<number | null>(null);
let inflight: AbortController | null = null;
let timer: ReturnType<typeof setTimeout> | null = null;
let failures = 0;
let requestSeq = 0;
let started = false;

function clearTimer(): void {
  if (timer !== null) clearTimeout(timer);
  timer = null;
}

function isAbort(err: unknown): boolean {
  return err instanceof DOMException && err.name === 'AbortError';
}

async function load(): Promise<void> {
  inflight?.abort();
  const ac = new AbortController();
  inflight = ac;
  const seq = ++requestSeq;
  status = 'loading';
  try {
    const res = await fetch(SOLAR_SITES_API, { signal: ac.signal });
    if (!res.ok) throw new Error(`Solar sites feed returned HTTP ${res.status}`);
    const payload = (await res.json()) as unknown;

    if (seq !== requestSeq) return;
    const normalized = normalizeSolarPayload(payload);
    all = normalized.sites;
    rejected = normalized.rejected;
    inventoryReason = normalized.reason;
    lastUpdated = Date.now();
    failures = 0;
    error = null;
    status = 'ok';
  } catch (err) {
    if (isAbort(err) || seq !== requestSeq) return;
    failures++;
    error = err instanceof Error ? err.message : String(err);
    status = 'stale';
    console.error('solarSites.load failed:', err);
  } finally {
    if (inflight === ac) inflight = null;
  }
}

async function cycle(): Promise<void> {
  await load();
  if (!started) return;
  clearTimer();
  timer = setTimeout(() => void cycle(), nextSolarDelay(failures));
}

function onVisibility(): void {
  if (!started) return;
  if (document.hidden) {
    clearTimer();
    return;
  }
  const age = lastUpdated === null ? Number.POSITIVE_INFINITY : Date.now() - lastUpdated;
  if (age >= SOLAR_POLL_MS) void cycle();
  else {
    clearTimer();
    timer = setTimeout(() => void cycle(), SOLAR_POLL_MS - age);
  }
}

export const solarSites = {
  get all() {
    return all;
  },
  get count() {
    return all.length;
  },
  get status() {
    return status;
  },
  get error() {
    return error;
  },
  get inventoryReason() {
    return inventoryReason;
  },
  get rejected() {
    return rejected;
  },
  get lastUpdated() {
    return lastUpdated;
  },
  start(): void {
    if (started) return;
    started = true;
    if (typeof document !== 'undefined')
      document.addEventListener('visibilitychange', onVisibility);
    void cycle();
  },
  stop(): void {
    started = false;
    clearTimer();
    inflight?.abort();
    inflight = null;
    if (typeof document !== 'undefined')
      document.removeEventListener('visibilitychange', onVisibility);
  },
  refresh(): Promise<void> {
    failures = 0;
    return cycle();
  },
};
